import { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, Space, Popconfirm, message, Collapse, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, GlobalOutlined, LinkOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import API from '../api/client';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link', 'image'],
    ['blockquote', 'code-block'],
    ['clean'],
  ],
};

const categoryOptions = [
  { value: 'kvkk', label: 'KVKK' },
  { value: 'kurumsal', label: 'Kurumsal' },
  { value: 'genel', label: 'Genel' },
];

const categoryColors = { kvkk: 'red', kurumsal: 'blue', genel: 'default' };

const PagesPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try { const res = await API.get('/pages'); setData(res.data); }
    catch { message.error('Veriler yüklenemedi'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const openCreate = () => {
    setEditingId(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditingId(record._id);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingId) {
        await API.put(`/pages/${editingId}`, values);
        message.success('Güncellendi');
      } else {
        await API.post('/pages', values);
        message.success('Eklendi');
      }
      setModalOpen(false);
      fetchData();
    } catch { /* validation */ }
  };

  const handleDelete = async (id) => {
    await API.delete(`/pages/${id}`);
    message.success('Silindi');
    fetchData();
  };

  const columns = [
    { title: 'Sayfa Adı', dataIndex: 'title', key: 'title' },
    {
      title: 'URL', dataIndex: 'slug', key: 'slug',
      render: (slug, record) => slug ? (
        <span style={{ fontSize: 12, color: '#888' }}>
          <LinkOutlined /> /{slug}
        </span>
      ) : '-',
    },
    {
      title: 'Kategori', dataIndex: 'category', key: 'category',
      render: (cat) => <Tag color={categoryColors[cat] || 'default'}>{cat?.toUpperCase() || '-'}</Tag>,
    },
    {
      title: 'İşlem', key: 'actions', width: 120,
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} size="small" onClick={() => openEdit(record)} />
          <Popconfirm title="Silmek istediğinize emin misiniz?" onConfirm={() => handleDelete(record._id)}>
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Sayfa Yönetimi</h2>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Yeni Sayfa</Button>
      </div>
      <Table columns={columns} dataSource={data} rowKey="_id" loading={loading} />
      <Modal
        title={editingId ? 'Sayfa Düzenle' : 'Yeni Sayfa'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        width={800}
        okText="Kaydet"
        cancelText="İptal"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="title" label="Sayfa Başlığı" rules={[{ required: true, message: 'Başlık gerekli' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="slug" label="URL Slug" extra="Boş bırakılırsa başlıktan otomatik oluşturulur">
            <Input addonBefore="/" placeholder="otomatik-olusturulur" />
          </Form.Item>
          <Form.Item name="category" label="Kategori" rules={[{ required: true, message: 'Kategori gerekli' }]}>
            <Select options={categoryOptions} />
          </Form.Item>
          <Form.Item name="content" label="Sayfa İçeriği" rules={[{ required: true, message: 'İçerik gerekli' }]}>
            <ReactQuill theme="snow" modules={quillModules} placeholder="Sayfa içeriğini buraya yazın..." style={{ minHeight: 300 }} />
          </Form.Item>

          <Collapse
            ghost
            items={[{
              key: 'seo',
              label: <span><GlobalOutlined /> SEO Ayarları</span>,
              children: (
                <>
                  <Form.Item name="metaTitle" label="Meta Başlık" extra="Tarayıcı sekmesinde ve arama sonuçlarında görünecek başlık">
                    <Input placeholder="Sayfa başlığı | Dihiz" />
                  </Form.Item>
                  <Form.Item name="metaDescription" label="Meta Açıklama" extra="Arama motorlarında görünecek açıklama (max 160 karakter)">
                    <Input.TextArea rows={2} placeholder="Sayfa hakkında kısa açıklama..." maxLength={160} showCount />
                  </Form.Item>
                  <Form.Item name="metaKeywords" label="Anahtar Kelimeler" extra="Virgülle ayırarak yazın">
                    <Input placeholder="dihiz, didim, kvkk" />
                  </Form.Item>
                </>
              ),
            }]}
          />
        </Form>
      </Modal>
    </>
  );
};

export default PagesPage;
